'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Wrapper, SlideContent } from './styles';
import styles from './styles.css';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { useRouter } from 'next-nprogress-bar';
import { useCurrentLocale } from '@locales/client';

import { useAuthModal } from '@/shared/hooks';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useBanners } from './model/hooks';
import { BannerPrompts } from '@/features/data-managment/ui/banner-prompts';
import { LazyMedia } from './ui/lazy-media';
import { MotivationalModal } from './ui/motivational-modal';
import { useLocaleFormat } from '@/shared/hooks';
import { useVisibleState } from '@/shared/hooks';
import { useBonuses } from '@/features/profile/model/hooks';
import { BannerItem } from '@/features/news-managment/ui/banner-item';
import { useMemo } from 'react';
import { BonusItem } from '@/features/news-managment/ui/bonus-item';

const News = ({ mobileLandscape }) => {
  const locale = useCurrentLocale();
  const router = useRouter();
  const { user } = useUser();
  const { data: bannersData, isLoading } = useBanners({ enabled: user.isAuth });
  const {
    data: bonusData,
    isLoading: bonusDataLoading,
    mutateBonuses,
  } = useBonuses();
  const {
    open: openMotivationalModal,
    visible: visibleMotivationalModal,
    close: closeMotivationalModal,
  } = useVisibleState(false);
  const { handleOpenAuth } = useAuthModal();
  const formatUrl = useLocaleFormat();

  const isActiveMotivationalModal=user.isAuth &&
    !user?.hasPaymentInfo &&
    (locale === 'ro' || locale === 'en')

  const data = useMemo(() => {
    if (!bannersData) {
      return bonusData;
    }
    if (!bonusData) {
      return bannersData;
    }

    const result = [];
    const availableBonuses = bonusData.filter(
      (bonus) => bonus.state === 'available'
    );
    const maxLength = Math.max(bannersData.length, availableBonuses.length);

    for (let i = 0; i < maxLength; i++) {
      if (i < bannersData.length) {
        result.push({ type: 'banner', data: bannersData[i] });
      }
      if (i < availableBonuses.length) {
        result.push({ type: 'bonus', data: availableBonuses[i] });
      }
    }

    return result;
  }, [bannersData, bonusData]);

  const isExternalLink = (url) =>
    url.startsWith('http://') || url.startsWith('https://');

  const handleClick = (url: string) => {
    const path = formatUrl(url);
    if (isExternalLink(url)) {
      window.open(path, '_blank');
    } else {
      router.push(path);
    }
  };

  const handleBonusClick = (bonus) => {
    router.push(`/${locale}/profile/bonuses?bonusID=${bonus.id}`);
  };

  const pagination = mobileLandscape
    ? {
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className}"></span>`,
      }
    : false;

  if (!user.isAuth) {
    const videoSources = mobileLandscape
      ? `/banners/mobile/${locale}.mp4`
      : `/banners/desktop/${locale}.mp4`;

    const imageSrc = mobileLandscape
      ? `/images/background/banners/registration-banner-${locale}-mobile.jpg`
      : `/images/background/banners/registration-banner-${locale}.jpg`;

    return (
      <Wrapper>
        <Swiper
          loop
          loopAdditionalSlides={1}
          breakpoints={{
            640: { slidesPerView: 1, spaceBetween: 0 },
            768: { slidesPerView: 1, spaceBetween: 20 },
            960: { slidesPerView: 1, spaceBetween: 20 },
            1132: { slidesPerView: 1, spaceBetween: 20 },
          }}
          keyboard={{ enabled: true }}
          autoplay={{ delay: 1000000, stopOnLastSlide: true }}
          loopPreventsSliding={false}
          className={styles.swiper}
          modules={[Keyboard, Autoplay, Pagination]}
          autoHeight
        >
          <SwiperSlide>
            <SlideContent
              style={{ padding: 0 }}
              onClick={() => handleOpenAuth()}
              $locale={locale}
            >
              <LazyMedia
                videoSrc={videoSources}
                imageSrc={imageSrc}
                alt="Welcome banner"
                mobileLandscape={mobileLandscape}
              />
            </SlideContent>
          </SwiperSlide>
        </Swiper>
      </Wrapper>
    );
  }
  if (
    isActiveMotivationalModal
  ) {
    const videoSources = null;
    
    // mobileLandscape
    //   ? `/banners/mobile/${locale}.mp4`
    //   : `/banners/desktop/${locale}.mp4`;

    const imageSrc = mobileLandscape
      ? `/images/background/banners/motivational/motivational-banner-${locale}-mobile.png`
      : `/images/background/banners/motivational/motivational-banner-${locale}-desktop.png`;

    return (
      <Wrapper>
        <Swiper
          loop
          loopAdditionalSlides={1}
          breakpoints={{
            640: { slidesPerView: 1, spaceBetween: 0 },
            768: { slidesPerView: 1, spaceBetween: 20 },
            960: { slidesPerView: 1, spaceBetween: 20 },
            1132: { slidesPerView: 1, spaceBetween: 20 },
          }}
          keyboard={{ enabled: true }}
          autoplay={{ delay: 1000000, stopOnLastSlide: true }}
          loopPreventsSliding={false}
          className={styles.swiper}
          modules={[Keyboard, Autoplay, Pagination]}
          autoHeight
        >
          <SwiperSlide>
            <SlideContent
              style={{ padding: 0 }}
              onClick={() =>openMotivationalModal()}
              $locale={locale}
            >
              <LazyMedia
                videoSrc={videoSources}
                imageSrc={imageSrc}
                styleForBox={{
                  height: '100%',
                  width: '100%',
                  aspectRatio: mobileLandscape ? '76 / 49' : '283 / 55',
                }}
                alt="motivational-banner"
                mobileLandscape={mobileLandscape}
              />
            </SlideContent>
          </SwiperSlide>
        </Swiper>
        {visibleMotivationalModal&& <MotivationalModal
          visible={visibleMotivationalModal}
          onClose={closeMotivationalModal}
        />}
       
      </Wrapper>
    );
  }

  if (isLoading || bonusDataLoading) return null;

  return (
    <Wrapper>
      <BannerPrompts />
      <Swiper
        loop
        key={locale}
        loopAdditionalSlides={1}
        breakpoints={{
          640: { slidesPerView: 1, spaceBetween: 0 },
          768: { slidesPerView: 3, spaceBetween: 20 },
          960: { slidesPerView: 3, spaceBetween: 20 },
          1132: { slidesPerView: 3, spaceBetween: 20 },
        }}
        keyboard={{ enabled: true }}
        autoplay={{ delay: 1000000, stopOnLastSlide: true }}
        loopPreventsSliding={false}
        pagination={pagination}
        className={styles.swiper}
        modules={[Keyboard, Autoplay, Pagination]}
      >
        {data?.map((item) => {
          if (item.type === 'banner') {
            return (
              <SwiperSlide key={`banner_${item.data?.id}`}>
                <BannerItem
                  banner={item.data}
                  handleClick={handleClick}
                  mobileLandscape={mobileLandscape}
                  router={router}
                />
              </SwiperSlide>
            );
          }

          if (item.type === 'bonus') {
            return (
              <SwiperSlide key={`bonus_${item.data?.id}`}>
                <BonusItem
                  bonus={item.data}
                  handleClick={handleBonusClick}
                  locale={locale}
                  updateDate={() => mutateBonuses()}
                />
              </SwiperSlide>
            );
          }

          return null;
        })}
      </Swiper>
    </Wrapper>
  );
};

const NewsWithBreakpointSlice = withBreakpointsSlice(
  News,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { NewsWithBreakpointSlice as News };
